import { useState } from 'react';
import { toast } from 'sonner';

import { updateBookmark } from '@/api/bookmarks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Bookmark } from '@/types/models';

interface Props {
  open: boolean;
  onClose: () => void;
  bookmarks: Bookmark[];
  onUpdated: () => void;
}

function parseTags(input: string): string[] {
  return input
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

export function ManageTagsModal({ open, onClose, bookmarks, onUpdated }: Props) {
  const [add, setAdd] = useState('');
  const [remove, setRemove] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setAdd('');
    setRemove('');
  };

  function close() {
    if (submitting) return;
    reset();
    onClose();
  }

  async function handleApply() {
    const addTags = parseTags(add);
    const removeTags = new Set(parseTags(remove));
    if (addTags.length === 0 && removeTags.size === 0) {
      toast.error('Enter at least one tag to add or remove.');
      return;
    }
    setSubmitting(true);
    let succeeded = 0;
    const failed: string[] = [];
    for (const b of bookmarks) {
      // (current ∪ add) ∖ remove, deduped + sorted is normalised by the API.
      const currentSet = new Set(b.tags);
      for (const t of addTags) currentSet.add(t);
      for (const t of removeTags) currentSet.delete(t);
      const newTags = Array.from(currentSet);
      try {
        await updateBookmark(b.id, { tags: newTags });
        succeeded += 1;
      } catch (err) {
        failed.push(`${b.title}: ${(err as Error).message}`);
      }
    }
    setSubmitting(false);
    if (failed.length === 0) {
      toast.success(`Tags updated on ${succeeded} bookmark${succeeded === 1 ? '' : 's'}`);
    } else {
      toast.error(`${succeeded} updated, ${failed.length} failed`);
    }
    onUpdated();
    reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Manage tags on {bookmarks.length} bookmark{bookmarks.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Tags are applied per bookmark: each row gets <code>(current ∪ add) ∖ remove</code>. Adds
            preserve the bookmark's other tags; removes only affect the listed ones.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="manage-tags-add">Tags to add (comma-separated)</Label>
            <Input
              id="manage-tags-add"
              autoFocus
              value={add}
              onChange={(e) => setAdd(e.target.value)}
              placeholder="research, archived"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="manage-tags-remove">Tags to remove (comma-separated)</Label>
            <Input
              id="manage-tags-remove"
              value={remove}
              onChange={(e) => setRemove(e.target.value)}
              placeholder="todo, draft"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={submitting}>
            {submitting ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
